import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

// ERIO-style grouped top-bar navigation for the admin console.
// The 40 panels are organised into 8 role-aware dropdown menus. A group is
// shown when its group-flag is on AND it has at least one visible item; each
// item may also carry its own ui_flag. Panels/test-ids are unchanged — only
// the navigation is reorganised.
const slug = (s) => s.toLowerCase().replace(/ /g, "-").replace(/&/g, "and");

const GROUPS = [
  {
    key: "crm", label: "CRM & Leads", groupFlag: "grp_crm",
    items: [
      { tab: "Relationship Hub", itemFlag: "show_crm_hub" },
      { tab: "Contacts", countKey: "contacts" },
      { tab: "Leads" },
      { tab: "Brochure requests", countKey: "brochures" },
      { tab: "Resource leads", countKey: "resourceLeads" },
      { tab: "Newsletter", countKey: "subscribers" },
      { tab: "Assessment leads" },
      { tab: "Preview leads" },
      { tab: "Portfolio leads" },
      { tab: "Lead backup" },
    ],
  },
  {
    key: "assessments", label: "Assessments", groupFlag: "grp_assessments",
    items: [
      { tab: "Assessments" },
      { tab: "Self-assessment" },
      { tab: "Stakeholder report" },
      { tab: "Heatmap" },
      { tab: "Inter-rater" },
    ],
  },
  {
    key: "enterprise360", label: "Enterprise360", groupFlag: "grp_enterprise360",
    items: [
      { tab: "Enterprise360" },
      { tab: "Organisations" },
      { tab: "Calibration map" },
      { tab: "ERP blueprints" },
      { tab: "Delivery TOM" },
      { tab: "Governance" },
    ],
  },
  {
    key: "content", label: "Content & Marketing", groupFlag: "grp_content",
    items: [
      { tab: "Resources", countKey: "resources" },
      { tab: "Toolkits" },
      { tab: "Site content" },
      { tab: "Case studies" },
      { tab: "Brand assets" },
      { tab: "SEO" },
      { tab: "OG copy" },
      { tab: "Training" },
    ],
  },
  {
    key: "commercial", label: "Commercial", groupFlag: "grp_commercial",
    items: [
      { tab: "Rate cards", itemFlag: "show_rate_card_master" },
      { tab: "Deal rate cards", itemFlag: "show_rate_card_deal" },
      { tab: "Trials", itemFlag: "show_trials_review" },
      { tab: "Demo access", itemFlag: "show_trials_review" },
      { tab: "Campaigns", itemFlag: "show_campaigns" },
    ],
  },
  {
    key: "operations", label: "Operations", groupFlag: "grp_operations",
    items: [
      { tab: "Ops" },
      { tab: "Proof dashboard" },
      { tab: "Playbook & cert" },
      { tab: "Draft nudges" },
      { tab: "Email" },
      { tab: "Searches" },
      { tab: "Cross-app" },
      { tab: "Instance" },
      { tab: "Environment health" },
    ],
  },
  {
    key: "governance", label: "Governance & Access", groupFlag: "grp_governance",
    items: [
      { tab: "Users" },
      { tab: "Content governance", founderOnly: true, countKey: "contentSignoff", alert: true },
      { tab: "Docs access", founderOnly: true },
      { tab: "Audit log", itemFlag: "show_audit_log" },
      { tab: "Legal & IP", itemFlag: "show_legal_ip_tab" },
    ],
  },
  {
    key: "help", label: "Help", groupFlag: "grp_help",
    items: [
      { tab: "Help" },
      { tab: "User manual", dotKey: "manualUnread", manual: true },
    ],
  },
];

const AdminNav = ({ me, tab, setTab, counts = {}, manualUnread, openManual }) => {
  const [open, setOpen] = useState(null); // open group key
  const ref = useRef(null);

  // ui_flags fall back to "everything visible" when /me hasn't loaded yet,
  // so we never briefly hide founder tabs on cold start (mirrors Admin.jsx).
  const flag = (name) => (me ? !!me.ui_flags?.[name] : true);
  const isFounder = me ? me.platform_role === "founder" : true;

  const itemVisible = (it) => {
    if (it.founderOnly) return isFounder;
    if (it.itemFlag) return flag(it.itemFlag);
    return true;
  };
  const visibleGroups = GROUPS
    .filter((g) => flag(g.groupFlag))
    .map((g) => ({ ...g, items: g.items.filter(itemVisible) }))
    .filter((g) => g.items.length > 0);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(null); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(null); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, []);

  const activeGroupKey = visibleGroups.find((g) => g.items.some((it) => it.tab === tab))?.key;

  const pick = (it) => {
    if (it.manual) { openManual?.(); } else { setTab(it.tab); }
    setOpen(null);
  };

  return (
    <div className="border-b ek-line ek-surface" ref={ref} data-testid="admin-nav">
      <div className="ek-container flex flex-wrap items-stretch gap-1">
        {visibleGroups.map((g) => {
          const isOpen = open === g.key;
          const isActive = activeGroupKey === g.key;
          return (
            <div key={g.key} className="relative">
              <button
                type="button"
                data-testid={`admin-nav-group-${g.key}`}
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : g.key)}
                className={`flex items-center gap-1.5 px-4 py-3.5 text-[12px] font-medium tracking-[0.16em] uppercase border-b-2 transition-colors ${
                  isActive || isOpen ? "border-[#7737F6] ek-text" : "border-transparent ek-text-muted hover:text-black"
                }`}
              >
                {g.label}
                <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>

              {isOpen && (
                <div
                  data-testid={`admin-nav-menu-${g.key}`}
                  className="absolute left-0 top-full z-40 mt-px min-w-[240px] border ek-line ek-surface shadow-xl py-1.5"
                >
                  {g.items.map((it) => {
                    const active = it.tab === tab;
                    const count = it.countKey ? counts[it.countKey] : undefined;
                    const showDot = it.dotKey === "manualUnread" && manualUnread;
                    const isAlert = it.alert && typeof count === "number" && count > 0;
                    return (
                      <button
                        key={it.tab}
                        type="button"
                        data-testid={`admin-tab-${slug(it.tab)}`}
                        onClick={() => pick(it)}
                        className={`w-full text-left px-4 py-2.5 text-[13px] flex items-center justify-between gap-3 transition-colors ${
                          active ? "bg-[#7737F6]/8 text-[#7737F6] font-medium" : "ek-text-soft hover:bg-zinc-50"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          {it.tab}
                          {showDot && (
                            <span data-testid="admin-tab-manual-dot" className="h-2 w-2 rounded-full bg-[#7737F6]" />
                          )}
                        </span>
                        {isAlert ? (
                          <span data-testid={`admin-tab-alert-${slug(it.tab)}`}
                            className="text-[11px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                            {count}
                          </span>
                        ) : (typeof count === "number" && (
                          <span className="text-[11px] ek-text-muted tabular-nums">{count}</span>
                        ))}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminNav;
